/******************************************************************************
File: cgp-grid.component.ts
Author: Jared Craig - jared.craig@gm.com
Date: 10-10-2017
Description: Generic, reusable interface for kendo grid.
Other contributors: Neel Radia
******************************************************************************/

import { Component, OnInit, Input, ViewChild, OnDestroy, AfterViewChecked, HostListener, ElementRef, AfterViewInit } from '@angular/core';
import { process } from '@progress/kendo-data-query';
import { GridDataResult, FilterService, DataStateChangeEvent, GridComponent } from '@progress/kendo-angular-grid';
import { BehaviorSubject, Observable, of } from 'rxjs';

// CGP Dependencies
import { CgpConfirmationDialogService } from '../cgp-confirmation-dialog/cgp-confirmation-dialog.service';
import { CgpSnackbarService } from '../cgp-snackbar/cgp-snackbar.service';
import { CgpAlertDialogService } from '../cgp-alert-dialog/cgp-alert-dialog.service';
import { DataGridTemplate, flatten } from './data-grid.template';
import { messages, exceptions, exceptionTypes } from './data-grid.config';
import { Subject } from 'rxjs';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { ColumnTemplatePipe } from './data-grid.pipe';
import { FormBuilder } from '@angular/forms';
import { ConfigurationService } from './configuration/configuration.service';

import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';

const OFFSET_HEIGHT: number = 132;
const ROW_HEIGHT: number = 48;
const MIN_COLUMN_WIDTH: number = 160;
@Component({
    selector: 'data-grid',
    templateUrl: './data-grid.component.html',
    styleUrls: ['./data-grid.component.scss'],
})
export class DataGridComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {
    /**
     * 
     * @param cgpConfirmationDialogService 
     * @param cgpSnackbarService 
     * @param cgpAlertDialogService 
     */
    constructor(
        private cgpConfirmationDialogService: CgpConfirmationDialogService,
        private cgpSnackbarService: CgpSnackbarService,
        private cgpAlertDialogService: CgpAlertDialogService,
        private formBuilder: FormBuilder,
        private configurationService: ConfigurationService,
        private router: Router,
        private elRef: ElementRef
    ) {
    }
    /**
     * 
     */
    @ViewChild('grid', { static: true }) grid: GridComponent;
    /**
     * 
     */
    filterForm = this.formBuilder.group({ filter: [] });
    /**
     * Configuration dialog visibility
     */
    isShowConfig: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    /**
     * 
     */
    protected gridData: Observable<GridDataResult>;
    /**
     * 
     */
    private gridSize: number;
    /**
     * 
     */
    private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
    /**
     * 
     */
    private isReordered: boolean;
    /**
     * 
     */
    @Input() template: DataGridTemplate;
    /**
     * 
     */
    @Input() parentEditDataItem: any;
    /**
     * 
     */
    private exportDate: string;
    /**
     * 
     */
    private gridHeight: number = OFFSET_HEIGHT;
    /**
     * 
     */
    private showcaseBody: any;
    /**
     * 
     */
    ngAfterViewInit(): void {
        if (this.template.saveUserSettings) {
            this.showcaseBody = this.elRef.nativeElement;
            let el = this.showcaseBody;
            let counter = 0;
            do {
                counter = counter + 1;
                el = el.offsetParent;
                if (!el || !el.children) return;
                if (el.children.length && el.children[1].className === "showcase-body") {
                    this.showcaseBody = el.children[1];
                    return;
                }
            } while (counter < 10);
        }
    }

    ngOnInit() {
        if (this.template) {
            this.template.httpResponse
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe(x => {
                    if (x) this.handleResponse(x.response, x.message);
                })
        }
        this.template.changed
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(x => {
                if (this.template.saveUserSettings && !this.configurationService.getValue() && !this.parentEditDataItem) {
                    this.template.isLoading.next(true);
                    this.configurationService
                        .pipe(takeUntil(this.ngUnsubscribe))
                        .subscribe((s) => {
                            if (s) {
                                this.refresh(true);
                            }
                        });
                } else {
                    this.refresh(true);
                }
            });

        this.filterForm.get('filter').valueChanges
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(x => this.handleFilterChange);
    }


    /**
     * 
     */
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();

        if (this.template.saveUserSettings) {
            this.configurationService.next(null);
        }
    }


    /**
     * 
     */
    ngAfterViewChecked(): void {
        if (this.grid && !this.isReordered && this.configurationService.value && this.grid.columns.first.orderIndex === this.grid.columns.last.orderIndex) {
            this.isReordered = true;
            if (this.template.reorderable) {
                this.template.columns = this.configurationService.value;
            }
        }
    }


    /**
     * 
     * @param state 
     */
    onStateChange(state: DataStateChangeEvent): void {
        if (isNaN(state.take)) {
            this.template.isAll = true;
            delete state.skip;
            delete state.take;
        } else {
            this.template.isAll = false;
        }

        if (this.template.gridState.take !== state.take) {
            this.template.gridState = state;
        }
        this.template.gridState = state;

        if (this.template.saveUserSettings) {
            this.configurationService.saveUserSettings(state);
        }

        const _dataResult = process(this.template.data, this.template.gridState);
        this.gridData = of(_dataResult);
        this.gridSize = _dataResult.total;
        this.refresh(false);
        this.getHeight(true, state);
    }


    /**
     * 
     * @param forceRefresh 
     */
    refresh(forceRefresh: boolean) {
        this.template.isLoading.next(true);
        const _options = {
            parentEditDataItem: this.parentEditDataItem,
            force: forceRefresh
        }
        this.template.read(_options)
            .then(response => {
                this.setGridData(response);
                this.template.isLoading.next(false);
                this.template.isSelectAll = false;
                this.template.selectedRows = [];
            })
            .catch(e => this.handleError(e, exceptions.gridRead))
    }


    /**
     * 
     */
    getColumnValue = (column: any, dataItem: any): string => {
        const _field: string = column.detail ? column.detail.field : column.field;
        const _obj: any = dataItem[_field];
        let response = _obj;

        if (!_obj) {
            response = dataItem;
            column.field
                .split('.')
                .forEach(f => response ? response = response[f] : null);
        }

        if (_obj && column.detail) {
            response = _obj[column.detail.textField];
        } else if (column.template && column.template !== 'date') {
            response = new ColumnTemplatePipe()
                .transform(column, dataItem);
        }
        return response;
    }


    /**
     * 
     */
    getMultiSelectFilterValue = (column, filter): string[] => {
        return flatten(filter).map(({ value }) => value.toString());
    }


    /**
     * 
     */
    getFilterValue = (column, filter): string[] => {
        const _value = filter.filters.value;
        return _value ? [_value] : this.getMultiSelectFilterValue(column, filter);
    }


    /**
     * 
     */
    getComboboxFilterValue = (column, filter): any => {
        const _filter = filter.filters.find(f => true);
        return _filter ? _filter.value : _filter;
    }


    /**
     * 
     */
    handleFilterChange = (value: string, column: any, filterService: FilterService) => {
        filterService.filter({
            filters: [{
                field: column.field,
                operator: 'contains',
                value
            }],
            logic: 'or'
        });
    }


    /**
     * 
     */
    handleMultiselectFilterChange = (values: any[], column: any, filterService: FilterService) => {
        filterService.filter({
            filters: values.map(value => ({
                field: column.field,
                operator: 'contains',
                value
            })),
            logic: 'or'
        });
    }


    /**
     * Inline grid (cancel) event handler
     */
    cancelHandler = (obj: any) => {
        if (obj) this.closeEditor(obj.sender, obj.rowIndex)
        this.template.isEditing.next(false);
    }


    /**
     * Grid (add) event handler
     * Issue template's (onCreate) callback
     * If editing inline, open new row for editing to top of grid
     */
    addHandler = ({ sender, dataItem }) => {
        this.template.onCreate(dataItem);

        if (!this.template.modal) {
            this.closeEditor(sender);
            if (this.template.editForm) {
                sender.addRow(this.template.editForm);
            } else {
                this.handleError(exceptions.editForm, exceptionTypes.undefined)
            }
        }
    }


    /**
     * Grid (edit) event handler
     * Issue template's (onCreate) callback
     * If editing inline, open selected grid row for editing
     */
    editHandler = ({ sender, rowIndex, dataItem }) => {
        const _lastRow = this.template.editedRowIndex;
        this.template.onEdit(dataItem, rowIndex);

        if (!this.template.modal) {
            this.closeEditor(sender, _lastRow);
            sender.editRow(rowIndex, this.template.editForm);
            this.template.isEditing.next(true);
        }
    }


    /**
     * Grid (save) event handler
     * Issue template's (onSave) callback
     */
    saveHandler = ({ sender, rowIndex, formGroup, isNew }) => {
        this.template.onSave({ dataItem: formGroup.value, isNew: isNew });
        this.closeEditor(sender, rowIndex);
        this.template.isEditing.next(false);
    }


    /**
     * Grid (remove) event handler
     * Prompt user with confirmation dialog
     * YES: Issue template's (delete) callback and close dialog
     * NO: Close dialog
     */
    removeHandler = ({ dataItem }) => {
        const source: string = this.template.getSource(dataItem);
        const customMessage = this.template.getDeleteMessage(dataItem);
        this.cgpConfirmationDialogService.showConfirmationDialog({
            title: 'Delete Confirmation',
            message: customMessage ? customMessage : messages.recordDeleteConfirm + source + '?',
            alert: 'danger',
            onYesClick: () => {
                const response = this.template.delete(dataItem);
                this.handleResponse(response, source + messages.recordDeleteSuccess);
                this.cgpConfirmationDialogService.closeConfirmationDialog();
            },
            onNoClick: () => {
                this.cgpConfirmationDialogService.closeConfirmationDialog();
            }
        });
    }

    /**
     * 
     */
    onColumnReorder(e) {
        this.configurationService.reorderColumns(e);
    }


    /**
     * 
     */
    onColumnResize(e) {
        this.configurationService.handleChange(e[0]);
    }


    /**
     * 
     */
    handleToolBarButtonClick = (button) => {
        button.callback()
        if (button.refresh) this.refresh(true);
    }


    /**
     * 
     */
    handleCommandButtonClick = (button, dataItem) => {
        button.callback(button, dataItem);
        if (button.refresh) this.refresh(true);
    }


    /**
     * 
     */
    onSelectionChange = (e) => {
        const item = this.template.selectedRows.find(x => x.index === e.index),
            index = item ? this.template.selectedRows.indexOf(item) : -1;

        if (this.template.selectableSettings.mode == 'single') {
            this.template.selectedRows = e.selectedRows;
        } else {
            if (e.selected) {
                if (index < 0) this.template.selectedRows.push(e.selectedRows[0])
            } else {
                this.template.selectedRows.splice(index, 1);
            }
        }

        this.template.select(this.template.selectedRows);
    }


    /**
     * 
     */
    isRowSelected = (e) => {
        return this.template.selectedRows.some(x => x.index === e.index);
    }


    /**
     * 
     */
    isShowCommandColumn = (): boolean => {
        try {
            const ctrl = this.template.controls;
            return ctrl && (ctrl.edit || ctrl.delete || (ctrl.customButtons && ctrl.customButtons.length > 0));
        } catch (e) {
            return true;
        }
    }


    /**
     * 
     */
    isFilterTemplate = (template: any): boolean => {
        return template && (
            template.type === 'multiselect' || template.filter === 'multiselect' ||
            template.type === 'combobox' || template.filter === 'combobox' ||
            template.type === 'form'
        );
    }


    /**
     * 
     * @param columnField 
     */
    isGroupingField(columnField: string) {
        const test = this.template.gridState.group.length > 0;
        const test2 = columnField == this.template.gridState.group[0].field;
        return test && test2;
    }


    /**
     * 
     */
    toggleSelectAll = (e) => {
        this.template.isSelectAll = e.target.checked;
        this.template.selectedRows = this.template.isSelectAll ?
            this.template.data.map((item, i) => {
                return {
                    dataItem: item,
                    index: i + this.template.gridState.skip
                }
            }) : [];
        this.template.select(this.template.selectedRows);
    }


    /**
     * 
     */
    getColumnWidth = (column, index) => {
        return column.field && !column.width ? MIN_COLUMN_WIDTH : column.width;
    }


    /**
     * 
     */
    getControlColumnWidth = () => {
        let baseWidth = 24;
        const width = 37;
        const ctrl = this.template.controls;
        if (ctrl.width) { return ctrl.width; }
        if (ctrl.edit) { baseWidth += width; }
        if (ctrl.delete) { baseWidth += width; }
        if (ctrl.customButtons) { baseWidth += (ctrl.customButtons.length * width); }
        if (!this.template.modal && !ctrl.delete) { baseWidth += width; }
        return baseWidth;
    }


    /**
     * 
     */
    getDetailRowHeader = (dataItem): string => {
        try {
            const dt = this.template.detailTemplate,
                header = dt.detailTitleField ?
                    (dataItem[dt.detailTitleField] + ': ') : '';
            return header + dt.title;
        } catch (e) {
            return "";
        }
    }


    /**
     * 
     */
    private getDatePath(): string {
        if (!this.exportDate) {
            const url: any[] = this.router.url.split('/');
            if (url.length > 0 && url[0] === '') url.shift();
            const date = new Date();
            this.exportDate = url.concat([date.getMonth() + 1, date.getDate(), date.getFullYear()])
                .join('_');
        }
        return this.exportDate;
    }


    /**
     * 
     */
    getExportFilename = (): string => {
        const extension = '.xlsx';
        const _fileName = this.getDatePath() + extension;

        try {
            const fileName: string = this.template.fileName ?
                this.template.fileName : _fileName;
            return fileName.toLowerCase().endsWith(extension) ?
                fileName : fileName + extension;
        } catch (e) {
            return 'grid.xlsx';
        }
    }


    /**
     * 
     */
    getPDFExportFilename = (): string => {
        const extension = '.pdf';
        const _fileName = this.getDatePath() + extension;

        try {
            const fileName: string = this.template.fileName ?
                this.template.fileName : _fileName;
            return fileName.toLowerCase().endsWith(extension) ?
                fileName : fileName + extension;
        } catch (e) {
            return 'grid.pdf';
        }
    }


    /**
     * 
     */
    isSelectAllDisabled = (): boolean => {
        return !this.template.data || this.template.data.length < 1;
    }


    /**
     * Show configuration dialog
     */
    showConfigDialog = () => this.isShowConfig.next(true);


    /**
     * 
     */
    isShowToolbar = (): boolean => {
        const tc = this.template.controls;
        return tc.add || tc.excel || tc.customToolBarButtons.length > 0;
    }


    /**
     * Excel Export - Exports all columns with applied filters
     */
    allData = (): ExcelExportData => {
        let data = this.template.data;
        if (!this.template.fileName) this.template.fileName = this.template.title;
        if (data.length > 0 && data[0] === undefined) data = [];

        return {
            data: process(data, {
                group: this.template.gridState.group,
                sort: this.template.gridState.sort,
                filter: this.template.gridState.filter
            }).data,
            group: this.template.gridState.group
        };
    }


    /**
     * 
     */
    isHidden = (isVisible: any): boolean => typeof (isVisible) === typeof (true) ? !isVisible : false;


    /**
     * 
     */

    @HostListener('window:resize')
    onResizeWindow() {
        this.getHeight(true);
    }

    private readonly calculateHeight = () => {
        let height: number;
        let rowHeightTotal = this.gridSize;
        if (rowHeightTotal > this.template.data.length) rowHeightTotal = this.template.data.length;
        if (rowHeightTotal > this.template.gridState.take) rowHeightTotal = this.template.gridState.take;
        height = OFFSET_HEIGHT + rowHeightTotal * ROW_HEIGHT;
        if (this.showcaseBody && this.showcaseBody.offsetHeight < height) {
            height = this.showcaseBody.offsetHeight;
        }
        return height;
    }

    /**
     * 
     */
    getHeight = (force: boolean = false, state: any = this.template.gridState): number => {
        if (!this.template.saveUserSettings || !this.grid || !this.template.data) return null;
        this.gridHeight = this.calculateHeight();
        return this.gridHeight;
    }

    /**
     * 
     * @param response 
     */
    private getResponseData(response): any[] {
        if (response && response.total) {
            this.template.recordCount = response.total;
        }
        return response && response.data && response.total ?
            response.data :
            response && response.length && response[0] ?
                response :
                [];
    }


    /**
     * 
     * @param response 
     */
    private setGridData(response) {
        this.template.data = this.getResponseData(response);
        const _dataResult: GridDataResult =
            this.template.isAll ?
                this.processAllData() :
                this.template.isServerSideFiltering ?
                    process(this.template.data, {
                        skip: this.template.gridState.skip,
                        group: this.template.gridState.group
                    }) :
                    process(this.template.data, this.template.gridState);
        if (response && response.total !== undefined) {
            _dataResult.total = response.total
        }
        this.gridData = of(_dataResult);
        this.gridSize = _dataResult.total;
    }


    /**
     * 
     */
    private processAllData(): GridDataResult {
        this.template.gridState.take = this.template.data.length;
        this.template.gridState.skip = 0;
        return process(this.template.data, this.template.gridState);
    }


    /**
     * 
     * @param grid 
     * @param rowIndex 
     */
    private closeEditor(sender: any, rowIndex = this.template.editedRowIndex) {
        sender.closeRow(rowIndex);
    }


    /**
     * 
     * @param response 
     * @param messageText 
     */
    private handleResponse(response: Promise<any>, messageText: string) {
        if (!response) { return; }
        response.then(x => {
            this.refresh(true);

            if (this.template.isNotificationsEnabled) {
                this.cgpSnackbarService.showSnackbar({
                    message: messageText,
                    duration: 2000
                });
            }
        })
            .catch(e => {
                this.refresh(true);
                this.handleError(e, `Http Error: ${e.status}`);
            });
    }


    /**
     * 
     * @param message 
     * @param title 
     */
    private handleError(e: any, title = 'Error') {
        this.template.isLoading.next(false);
        const _body = e.error ? e.error : e;
        if (this.cgpAlertDialogService.isOpen()) { return; }
        this.cgpAlertDialogService.showAlertDialog({
            title: title,
            message: _body,
            alert: 'danger',
            closeLabel: 'OK',
        });
    }

    isAutoLock = (): boolean => {
        return this.template.columns.filter(c => c['visible']).length > 8;
    }


    // To be overriden by child class
    private exportToExcel() { console.log('cliked parent excel') }

    // To be overriden by child class
    private exportToPdf() { }


    /**
     * 
     */
    private configBtn = {
        id: 'configBtn',
        title: 'Customize grid and column properties.',
        text: '',
        class: 'config-btn',
        iconClass: 'fa fa-fw fa-cog',
        callback: this.showConfigDialog,
        bare: true,
    };

    exportSplitBtn = {
        id: 'exportSplitBtn',
        title: 'export the grid',
        text: 'Export',
        iconClass: 'fa fa-fw fa-download',
        data: [{
            text: ' Excel',
            iconClass: 'fa fa-file-excel-o fa-fw',
        }, {
            text: 'PDF',
            iconClass: 'fa fa-file-pdf-o fa-fw',
        },]
    };

    isShowDetailTemplate = (dataItem: any, index: number): boolean => {
        return !!this.template.detailTemplate;
    }
} // End CGP Grid
